import "./select.css"
import * as SelectPrimitive from "@radix-ui/react-select"
import { Check, ChevronDown, ChevronUp } from "lucide-react"
import { useState } from "react"

import { cn } from "./utils"

interface Props {
  className?: string
  initialValue: string
  label: string
  moreOptions?: { key: string; label: string }[]
  moreOptionsIsActive?: boolean
  onChange: (value: string)=> void
  options: { key: string; label: string }[]
}

function SelectWrapped(input: Props) {
  const [showMore, setShowMore] = useState(false)
  const onChange = (value: string)=> input.onChange(value)
  return (
    <div
      className={cn("flex w-full shrink-0 flex-col gap-2.5", input.className)}
    >
      <div className="text-xs font-bold">{input.label}</div>
      <Select
        defaultValue={input.initialValue}
        key={input.initialValue}
        onValueChange={onChange}
      >
        <SelectTrigger>
          <SelectValue placeholder={input.label} />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            {input.options.map(option=> (
              <SelectItem key={option.key} value={option.key}>
                {option.label}
              </SelectItem>
            ))}
            {!showMore &&
              input.moreOptions?.length &&
              !input.moreOptionsIsActive && (
                <SelectShowMore
                  key={"@MORE"}
                  onClick={()=> setShowMore(true)}
                />
              )}
            {(showMore || input.moreOptionsIsActive) &&
              input.moreOptions?.map(option=> (
                <SelectItem key={option.key} value={option.key}>
                  {option.label}
                </SelectItem>
              ))}
          </SelectGroup>
        </SelectContent>
      </Select>
    </div>
  )
}

const Select = SelectPrimitive.Root

const SelectGroup = SelectPrimitive.Group

const SelectValue = SelectPrimitive.Value

function SelectContent({
  children,
  className,
  position = "popper",
  ref,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Content>) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        className={cn(
          `
            relative z-90 max-h-[35vh] min-w-32 origin-[--radix-select-content-transform-origin]
            overflow-hidden rounded-md border bg-popover text-primary-foreground shadow-md select-content
            sm:max-h-[50vh]
          `,
          position === "popper" &&
            `
              data-[side=bottom]:translate-y-1
              data-[side=left]:-translate-x-1
              data-[side=right]:translate-x-1
              data-[side=top]:-translate-y-1
            `,
          className,
        )}
        position={position}
        ref={ref}
        {...props}
      >
        <SelectScrollUpButton />
        <SelectPrimitive.Viewport
          className={cn(
            "max-h-[60vh] overflow-x-hidden overflow-y-auto p-1 sm:max-h-[80vh]",
            position === "popper" &&
              `h-(--radix-select-trigger-height) w-full min-w-(--radix-select-trigger-width)`,
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
        <SelectScrollDownButton />
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  )
}

function SelectItem({
  children,
  className,
  ref,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Item>) {
  return (
    <SelectPrimitive.Item
      className={cn(
        `
          relative flex w-full cursor-default items-center rounded-sm py-1 pr-2 pl-8 text-xs
          outline-none select-none sm:py-1.5
          focus:bg-accent-background focus:text-accent-foreground
          data-disabled:pointer-events-none data-disabled:opacity-50
        `,
        className,
      )}
      ref={ref}
      {...props}
    >
      <span className="absolute left-2 flex size-3.5 items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <Check className="size-4" />
        </SelectPrimitive.ItemIndicator>
      </span>

      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  )
}

function SelectLabel({
  className,
  ref,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Label>) {
  return (
    <SelectPrimitive.Label
      className={cn("py-1.5 pr-2 pl-8 text-sm font-semibold", className)}
      ref={ref}
      {...props}
    />
  )
}

function SelectScrollDownButton({
  className,
  ref,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollDownButton>) {
  return (
    <SelectPrimitive.ScrollDownButton
      className={cn(
        "flex cursor-default items-center justify-center py-1",
        className,
      )}
      ref={ref}
      {...props}
    >
      <ChevronDown className="size-4" />
    </SelectPrimitive.ScrollDownButton>
  )
}

function SelectScrollUpButton({
  className,
  ref,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollUpButton>) {
  return (
    <SelectPrimitive.ScrollUpButton
      className={cn(
        "flex cursor-default items-center justify-center py-1",
        className,
      )}
      ref={ref}
      {...props}
    >
      <ChevronUp className="size-4" />
    </SelectPrimitive.ScrollUpButton>
  )
}

function SelectSeparator({
  className,
  ref,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Separator>) {
  return (
    <SelectPrimitive.Separator
      className={cn("-mx-1 my-1 h-px bg-muted", className)}
      ref={ref}
      {...props}
    />
  )
}

function SelectShowMore({ onClick }: { onClick: ()=> void }) {
  return (
    <button
      className={cn(
        `
          relative flex w-full cursor-pointer items-center rounded-sm py-1.5 pr-2 pl-8 text-xs
          outline-none select-none
          hover:bg-accent-background hover:text-accent-foreground
        `,
      )}
      onClick={onClick}
      onPointerDown={e=> e.preventDefault()}
      type="button"
    >
      ...Show more...
    </button>
  )
}

function SelectTrigger({
  children,
  className,
  ref,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Trigger>) {
  return (
    <SelectPrimitive.Trigger
      className={cn(
        `
          data-placeholder:text-muted-foreground
          focus:ring-ring
          flex field-sizing-content h-8 w-fit max-w-full min-w-0 cursor-pointer items-center justify-between rounded-md
          border border-input bg-background px-3 py-2 text-xs text-primary-foreground
          ring-offset-background
          focus:ring-2 focus:ring-offset-2 focus:outline-none
          disabled:cursor-not-allowed disabled:opacity-50
          [&>span]:line-clamp-1 [&>span]:min-w-0
          transition-transform ease-[var(--ease-standard)]
          active:scale-[0.98]
        `,
        className,
      )}
      ref={ref}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild={true}>
        <ChevronDown className="size-4 opacity-50" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  )
}

export {
  SelectWrapped as Select,
  Select as SelectComponent,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectShowMore,
  SelectTrigger,
  SelectValue,
}
